import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { FileText, Send, Trash2, Edit3, Upload, X, Image as ImageIcon, BookOpen, Clock, Check, CheckSquare, Square, FileDown } from 'lucide-react';

const DraftsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [publishing, setPublishing] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await api.get('/quotations/all');
      setItems(res.data);
    } catch {
      toast.error('فشل تحميل الملخصات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleEdit = (draft) => {
    setEditing(draft.id);
    setEditText(draft.quotation_text || '');
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/quotations/${id}`, { quotation_text: editText });
      toast.success('تم الحفظ');
      setEditing(null);
      fetchItems();
    } catch {
      toast.error('فشل الحفظ');
    }
  };

  const handlePublish = async (id) => {
    setPublishing(id);
    try {
      await api.post(`/quotations/${id}/publish`);
      toast.success('تم نشر الملخص بنجاح');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل النشر');
    } finally {
      setPublishing(null);
    }
  };

  const handleDelete = async (id) => {
    const item = items.find(i => i.id === id);
    const isPub = item?.status === 'published';
    const msg = isPub
      ? 'هل تريد حذف هذا الاقتباس المنشور؟'
      : 'هل تريد حذف هذه المسودة؟';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/quotations/${id}`);
      toast.success('تم الحذف بنجاح');
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchItems();
    } catch {
      toast.error('فشل الحذف');
    }
  };

  const handleImageUpload = async (draftId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('يرجى اختيار صورة فقط'); return; }
    setUploading(draftId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/quotations/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await api.put(`/quotations/${draftId}`, { image_path: res.data.path, drive_url: res.data.drive_url || '' });
      toast.success('تم رفع الصورة');
      fetchItems();
    } catch {
      toast.error('فشل رفع الصورة');
    } finally {
      setUploading(null);
    }
  };

  const removeImage = async (draftId) => {
    try {
      await api.put(`/quotations/${draftId}`, { image_path: '' });
      toast.success('تمت إزالة الصورة');
      fetchItems();
    } catch {
      toast.error('فشل إزالة الصورة');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.id)));
    }
  };

  const handleDownloadPdf = async () => {
    if (selected.size === 0) {
      toast.error('اختر ملخصاً واحداً على الأقل');
      return;
    }
    setDownloading(true);
    try {
      const res = await api.post('/quotations/export-pdf', { ids: Array.from(selected) }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ملخصاتي.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('تم تنزيل الملف بنجاح');
    } catch {
      toast.error('فشل تنزيل الملف');
    } finally {
      setDownloading(false);
    }
  };

  const drafts = items.filter(i => i.status === 'draft');
  const published = items.filter(i => i.status === 'published');

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-40 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  const renderCard = (item, isDraft) => (
    <Card key={item.id} className={`p-6 space-y-4 hover:shadow-md transition-shadow ${selected.has(item.id) ? 'ring-2 ring-primary/40 bg-primary/[0.02]' : ''} ${!isDraft ? 'border-primary/10' : ''}`} data-testid={`quotation-card-${item.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => toggleSelect(item.id)} className="shrink-0 text-primary/70 hover:text-primary transition-colors" data-testid={`select-checkbox-${item.id}`}>
            {selected.has(item.id) ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5" />}
          </button>
          <BookOpen className={`w-6 h-6 shrink-0 ${isDraft ? 'text-amber-600' : 'text-primary'}`} />
          <div>
            <h3 className="font-bold text-accent text-lg">{item.title}</h3>
            <p className="text-sm text-muted-foreground">{item.author}{item.total_pages ? ` - ${item.total_pages} صفحة` : ''}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${isDraft ? 'bg-amber-50 text-amber-700' : 'bg-primary/10 text-primary'}`}>
          {isDraft ? 'مسوّدة' : 'منشور'}
        </span>
      </div>

      {isDraft && editing === item.id ? (
        <div className="space-y-3">
          <textarea value={editText} onChange={(e) => setEditText(e.target.value)} className="w-full min-h-[150px] p-4 rounded-xl border-2 border-primary/20 focus:border-primary bg-white resize-y text-base leading-relaxed focus:outline-none" dir="rtl" data-testid={`draft-edit-textarea-${item.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => handleSaveEdit(item.id)} data-testid={`draft-save-btn-${item.id}`}>حفظ</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>إلغاء</Button>
          </div>
        </div>
      ) : (
        <div className="bg-muted/30 rounded-xl p-4">
          {item.quotation_text ? (
            <p className="text-accent leading-relaxed whitespace-pre-wrap">{item.quotation_text}</p>
          ) : (
            <p className="text-muted-foreground/50 italic">لم تكتب شيئاً بعد...</p>
          )}
        </div>
      )}

      {isDraft && (
        item.image_path ? (
          <div className="flex items-center gap-2 bg-primary/5 rounded-lg p-3 border border-primary/20">
            <ImageIcon className="w-5 h-5 text-primary" />
            <span className="text-sm text-primary flex-1">صورة الكتاب مرفقة</span>
            <button onClick={() => removeImage(item.id)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <label className="cursor-pointer flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
            <Upload className={`w-4 h-4 ${uploading === item.id ? 'animate-pulse' : ''}`} />
            <span className="text-sm">{uploading === item.id ? 'جارٍ الرفع...' : 'إضافة صورة الكتاب'}</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(item.id, e)} disabled={uploading === item.id} />
          </label>
        )
      )}

      {!isDraft && item.image_path && (
        <div className="flex items-center gap-2 bg-primary/5 rounded-lg p-3 border border-primary/20">
          <ImageIcon className="w-5 h-5 text-primary" />
          <span className="text-sm text-primary">صورة الكتاب مرفقة</span>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-muted/50">
        {isDraft ? (
          <>
            <Button onClick={() => handlePublish(item.id)} disabled={publishing === item.id || !item.quotation_text?.trim()} size="sm" className="bg-primary hover:bg-primary/90" data-testid={`draft-publish-btn-${item.id}`}>
              <Send className="w-4 h-4 ml-1" />
              {publishing === item.id ? 'جارٍ النشر...' : 'نشر'}
            </Button>
            {editing !== item.id && (
              <Button size="sm" variant="outline" onClick={() => handleEdit(item)} data-testid={`draft-edit-btn-${item.id}`}>
                <Edit3 className="w-4 h-4 ml-1" />
                تعديل
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 mr-auto" data-testid={`draft-delete-btn-${item.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-1 text-primary text-sm">
            <Check className="w-4 h-4" />
            <span>منشور</span>
            <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 mr-2" data-testid={`published-delete-btn-${item.id}`} title="حذف نهائي">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
        <span className="text-xs text-muted-foreground mr-auto">
          {new Date(item.published_at || item.created_at).toLocaleDateString('ar-OM', { day: 'numeric', month: 'long' })}
        </span>
      </div>
    </Card>
  );

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6" data-testid="drafts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-2xl md:text-3xl font-bold text-accent">ملخصاتي</h1>
        </div>

        {items.length > 0 && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleSelectAll} className="gap-1.5" data-testid="select-all-btn">
              {selected.size === items.length ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
              {selected.size === items.length ? 'إلغاء التحديد' : 'تحديد الكل'}
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={selected.size === 0 || downloading} className="bg-primary hover:bg-primary/90 gap-1.5" data-testid="download-pdf-btn">
              <FileDown className="w-4 h-4" />
              {downloading ? 'جارٍ التنزيل...' : `تنزيل PDF ${selected.size > 0 ? `(${selected.size})` : ''}`}
            </Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-xl text-muted-foreground">لا توجد ملخصات حالياً</p>
          <p className="text-sm text-muted-foreground/70 mt-2">عند ختم كتاب، يمكنك كتابة ملخص استفادتك ومشاركة اقتباساتك المفضلة</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {drafts.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-amber-700 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                بانتظار النشر ({drafts.length})
              </h2>
              {drafts.map(d => renderCard(d, true))}
            </div>
          )}

          {published.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-primary flex items-center gap-2">
                <Check className="w-5 h-5" />
                منشورة ({published.length})
              </h2>
              {published.map(p => renderCard(p, false))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DraftsPage;
